// Monthly salary table (prototype's SbSalairesView): one row per
// employee — the month's facts from the validated grille, salaire
// habituel next to the salaire du mois (calculated, or typed by hand),
// the écart between them, T-end bonus + total columns, note cell
// opening the drawer, informed toggle and status chip. Read-only once
// the month is ENVOYE.

import { Fragment, useState } from 'react';
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_KEYS,
  type PaymentMethod,
  type PayrollEmployeeDTO,
  type PayrollLineStatus,
  type PayrollMonthDTO,
} from '@pointage/shared';
import {
  usePatchPayrollInformed,
  usePatchPayrollLine,
  usePatchPayrollPaymentMethod,
  usePatchPayrollSalary,
} from '../../api/hooks.js';
import { toast } from '../../store/toast.js';
import { fmtDA } from '../../lib/format.js';
import { MoneyButton, entityColor, entityShort, monthLabel, roleLabel, sbAvColor, sbInitials } from './common.js';
import { NoteDrawer, tagBg, tagColor } from './NoteDrawer.js';
import { FactChips, FactDetail } from './MonthFacts.js';

function Ecart({ diff }: { diff: number }) {
  if (diff === 0) return <span className="sb-ecart eq">= habituel</span>;
  return <span className={'sb-ecart gm-mono ' + (diff < 0 ? 'dn' : 'up')}>{diff > 0 ? '+' : '−'}{fmtDA(Math.abs(diff))}</span>;
}

function StatusChip({ st, canToggle, onToggle }: { st: PayrollLineStatus; canToggle: boolean; onToggle: () => void }) {
  const map: Record<PayrollLineStatus, [string, string, string]> = {
    SAISI: ['Saisi', 'var(--warns)', 'var(--warnc)'],
    VALIDE: ['Validé', 'var(--greens)', 'var(--greenc)'],
    ENVOYE: ['Envoyé', 'var(--shfs)', 'var(--shf)'],
  };
  const [lbl, bg, c] = map[st];
  return (
    <button
      type="button"
      className={'sb-st' + (canToggle ? ' can' : '')}
      style={{ background: bg, color: c }}
      onClick={canToggle ? onToggle : undefined}
      title={canToggle ? (st === 'SAISI' ? 'Cliquer pour valider' : 'Cliquer pour repasser en saisi') : ''}
    >
      {st === 'VALIDE' ? '✓ ' : ''}{lbl}
    </button>
  );
}

export function SalairesTable({
  data,
  team,
  canEdit,
  canEditNotes,
  onOpenQuarter,
}: {
  data: PayrollMonthDTO;
  team: PayrollEmployeeDTO[];
  canEdit: boolean; // payroll.edit && stage !== ENVOYE (salary/status)
  canEditNotes: boolean; // payroll.edit — notes stay editable at ENVOYE
  onOpenQuarter: (employeeId: string) => void;
}) {
  const [drawer, setDrawer] = useState<PayrollEmployeeDTO | null>(null);
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());
  const toggle = (id: string) =>
    setOpened((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const patchLine = usePatchPayrollLine();
  const patchSalary = usePatchPayrollSalary();
  const patchInformed = usePatchPayrollInformed();
  const patchMethod = usePatchPayrollPaymentMethod();

  const { year, month, isQuarterEnd, quarter } = data;
  const sent = data.stage === 'ENVOYE';
  const bonus = (e: PayrollEmployeeDTO) => (isQuarterEnd ? e.finalBonus : null);
  // A sent month shows what left in the bank file, frozen.
  const paid = (e: PayrollEmployeeDTO) => (sent ? e.paidSalary ?? e.salary : e.salary);
  // What the month pays without a hand-typed amount.
  const auto = (e: PayrollEmployeeDTO) => e.computedSalary ?? e.referenceSalary;
  const total = (e: PayrollEmployeeDTO) => paid(e) + (bonus(e) ?? 0);
  const totSal = team.reduce((a, e) => a + paid(e), 0);
  const totRef = team.reduce((a, e) => a + e.referenceSalary, 0);
  const anyValidated = team.some((e) => e.gridState === 'validated');
  const totOff = team.reduce((a, e) => a + (e.monthFacts?.offDays ?? 0), 0);
  const cols = 9 + (isQuarterEnd ? 3 : 0);
  const totBon = team.reduce((a, e) => a + (bonus(e) ?? 0), 0);

  const fail = (err: unknown, fallback: string) => {
    const e = err as Error & { body?: { error?: string; message?: string } };
    toast.danger(e.body?.message ?? (e.body?.error === 'locked' ? 'Mois envoyé — modification verrouillée.' : fallback));
  };

  // Drawer employee must track fresh query data (notes update live).
  const drawerEmp = drawer ? team.find((e) => e.id === drawer.id) ?? drawer : null;

  return (
    <div className="gm-gridwrap">
      <div className="gm-scroll" style={{ maxHeight: 'calc(100vh - 330px)' }}>
        <table className="sb-tbl">
          <thead>
            <tr>
              <th className="sb-hEmp">Employé <span className="gm-th-count">{team.length}</span></th>
              <th>Ce mois</th>
              <th className="sb-hnum">Salaire habituel</th>
              <th className="sb-hnum" title="Jours ouvrés non travaillés, retirés à 1/22 du salaire habituel">Non trav.</th>
              <th className="sb-hnum">Salaire du mois</th>
              <th>Écart</th>
              {isQuarterEnd && <th>Bonus T{quarter}</th>}
              {isQuarterEnd && <th>Total virement</th>}
              <th>Note · {monthLabel(month)}</th>
              {isQuarterEnd && <th>Informé</th>}
              <th>Paiement</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {team.map((e) => {
              const n = e.notes[month];
              const hasNote = !!n && (n.text !== '' || n.amount != null || n.tags.length > 0);
              const facts = e.monthFacts;
              const manual = e.salaryOverride != null;
              const showFormula = !sent && !manual && facts != null && facts.offDays > 0;
              const isOpen = opened.has(e.id) && (facts?.facts.length ?? 0) > 0;
              return (
                <Fragment key={e.id}>
                <tr className={'sb-row' + (manual && e.computedSalary != null && e.salaryOverride !== e.computedSalary ? ' sb-row-manual' : '')}>
                  <td className="sb-cEmp">
                    <div className="gm-emp" style={{ padding: '8px 16px' }}>
                      <span className="gm-av" style={{ background: sbAvColor(e.id) }}>{sbInitials(e.name)}</span>
                      <span className="gm-empmeta">
                        <span className="gm-name">{e.name}</span>
                        <span className="gm-role">
                          {roleLabel(e)} · <b style={{ color: entityColor(e.entityId) }}>{entityShort(e.entityId)}</b>
                        </span>
                      </span>
                    </div>
                  </td>
                  <td className="sb-cFacts">
                    <FactChips e={e} open={isOpen} onToggle={() => toggle(e.id)} />
                  </td>
                  <td className="sb-num sb-r"><span className="gm-mono sb-hab">{fmtDA(e.referenceSalary)}</span></td>
                  <td className="sb-num sb-r">
                    {facts ? (
                      <span className={'gm-mono ' + (facts.offDays > 0 ? 'sb-off' : 'sb-hab')}>{facts.offDays > 0 ? `${facts.offDays} j` : '0'}</span>
                    ) : (
                      <span className="sb-hab">—</span>
                    )}
                  </td>
                  <td className="sb-num sb-r">
                    <MoneyButton
                      value={paid(e)}
                      editable={canEdit}
                      onChange={(v) =>
                        // Typing back the automatic amount clears the
                        // manual one: the month follows the grille again.
                        patchSalary.mutateAsync({ year, month, employeeId: e.id, amount: v === auto(e) ? null : v })
                          .catch((err) => fail(err, 'Échec de la modification du salaire.'))
                      }
                    />
                    {showFormula && (
                      <span className="sb-formula gm-mono">
                        {fmtDA(e.referenceSalary)} − {facts.offDays} × {fmtDA(facts.dailyRate)}
                      </span>
                    )}
                  </td>
                  <td className="sb-cEcart">
                    <Ecart diff={paid(e) - e.referenceSalary} />
                    {manual && !sent && (
                      <span className="sb-manual">
                        ✎ Modifié à la main
                        {e.computedSalary != null && <> · calculé <b className="gm-mono">{fmtDA(e.computedSalary)}</b></>}
                        {canEdit && (
                          <button
                            type="button"
                            className="sb-manual-reset"
                            onClick={() =>
                              patchSalary.mutateAsync({ year, month, employeeId: e.id, amount: null })
                                .catch((err) => fail(err, 'Échec.'))
                            }
                          >
                            {e.computedSalary != null ? 'Revenir au calcul' : 'Rétablir'}
                          </button>
                        )}
                      </span>
                    )}
                  </td>
                  {isQuarterEnd && (
                    <td className="sb-num">
                      {bonus(e) != null ? (
                        <button type="button" className="sb-bonuschip gm-mono" onClick={() => onOpenQuarter(e.id)} title={`Voir dans Bonus T${quarter}`}>
                          +{fmtDA(bonus(e))}
                        </button>
                      ) : (
                        <button type="button" className="sb-bonusempty" onClick={() => onOpenQuarter(e.id)}>à définir</button>
                      )}
                    </td>
                  )}
                  {isQuarterEnd && (
                    <td className="sb-num"><b className="gm-mono sb-tot">{fmtDA(total(e))}</b></td>
                  )}
                  <td className="sb-note">
                    <button type="button" className="sb-notebtn" onClick={() => setDrawer(e)}>
                      {hasNote ? (
                        <>
                          {n!.amount != null && <span className="sb-noteamt gm-mono">+{fmtDA(n!.amount)}</span>}
                          <span className="sb-notetxt">{n!.text || '(montant seul)'}</span>
                          {n!.tags.slice(0, 2).map((k) => (
                            <span key={k} className="sb-minitag" style={{ background: tagBg(k), color: tagColor(k) }}>
                              {k === 'augm' ? 'Augmentation ?' : k.charAt(0).toUpperCase() + k.slice(1)}
                            </span>
                          ))}
                        </>
                      ) : (
                        <span className="sb-noteadd">+ note</span>
                      )}
                    </button>
                  </td>
                  {isQuarterEnd && (
                    <td className="sb-cInf">
                      <button
                        type="button"
                        className={'sb-inf' + (e.informed ? ' on' : '')}
                        onClick={() =>
                          patchInformed.mutateAsync({ year, quarter, employeeId: e.id, informed: !e.informed })
                            .catch((err) => fail(err, 'Échec.'))
                        }
                      >
                        {e.informed ? '✓ Oui' : '—'}
                      </button>
                    </td>
                  )}
                  <td className="sb-cPay">
                    <select
                      className={'sb-pay' + (e.paymentMethod !== 'VIREMENT' ? ' off' : '')}
                      value={e.paymentMethod}
                      disabled={!canEdit}
                      aria-label={`Mode de paiement — ${e.name}`}
                      data-testid={`pay-method-${e.id}`}
                      title="Réglage permanent de l'employé : seuls les virements partent dans le fichier banque"
                      onChange={(ev) =>
                        patchMethod.mutateAsync({ year, month, employeeId: e.id, method: ev.target.value as PaymentMethod })
                          .catch((err) => fail(err, 'Échec du changement de mode de paiement.'))
                      }
                    >
                      {PAYMENT_METHOD_KEYS.map((k) => <option key={k} value={k}>{PAYMENT_METHODS[k].label}</option>)}
                    </select>
                    {e.paymentMethod === 'VIREMENT' && !sent && (!e.bankAccount || !e.bankSwift) && (
                      <span className="sb-paywarn">⚠ RIB manquant</span>
                    )}
                  </td>
                  <td className="sb-cSt">
                    <StatusChip
                      st={e.status}
                      canToggle={canEdit && e.status !== 'ENVOYE'}
                      onToggle={() =>
                        patchLine.mutateAsync({
                          year, month, employeeId: e.id,
                          status: e.status === 'VALIDE' ? 'SAISI' : 'VALIDE',
                        }).catch((err) => fail(err, 'Échec du changement de statut.'))
                      }
                    />
                  </td>
                </tr>
                {isOpen && (
                  <tr className="sb-detail">
                    <td colSpan={cols}><FactDetail e={e} /></td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="sb-foot">
              <td className="sb-cEmp"><span className="gm-foot-lbl">Masse salariale</span></td>
              <td></td>
              <td className="sb-num sb-r"><span className="gm-mono sb-hab">{fmtDA(totRef)}</span></td>
              <td className="sb-num sb-r">{anyValidated && <span className="gm-mono sb-off">{totOff} j</span>}</td>
              <td className="sb-num sb-r"><b className="gm-mono" style={{ paddingRight: 8 }}>{fmtDA(totSal)}</b></td>
              <td><Ecart diff={totSal - totRef} /></td>
              {isQuarterEnd && <td className="sb-num"><b className="gm-mono" style={{ color: 'var(--shf)' }}>+{fmtDA(totBon)}</b></td>}
              {isQuarterEnd && <td className="sb-num"><b className="gm-mono sb-grand">{fmtDA(totSal + totBon)} DA</b></td>}
              <td></td>
              {isQuarterEnd && <td></td>}
              <td></td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      {drawerEmp && (
        <NoteDrawer data={data} emp={drawerEmp} month={month} canEdit={canEditNotes} onClose={() => setDrawer(null)} />
      )}
    </div>
  );
}
